import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useTransition } from '@react-spring/web';
import { useState } from 'react';

import {
  AveragePlayerScore,
  GameplayTier,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { css } from '@sorare/css';

import { NBAAnyCardFooter } from '#src/components/card/NBAAnyCardFooter/index.tsx';
import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { PlayerDrawer } from '#src/components/player/PlayerDrawer/index.tsx';

import type { CardInfos_anyCardInterface } from './__generated__/index.graphql.ts';

const footerWrapper = css`
  position: absolute;
  bottom: calc(-1 * var(--margin-bottom-center-area));
`;

type Props = {
  card: CardInfos_anyCardInterface;
  showPlayerDetails?: boolean;
  /** Keeps the footer above the stacked cards so the tier badge isn't hidden. */
  zIndex?: number;
};
export const CardInfos = ({ card, showPlayerDetails, zIndex }: Props) => {
  const [playerDetailsOpen, setPlayerDetailsOpen] = useState(false);
  const showNBACardFooter = card.anyPlayer.sport === Sport.NBA;
  const transition = useTransition(card, {
    from: { opacity: 0, transform: 'translate3d(0, -32px ,0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0 ,0)', delay: 300 },
    leave: {
      opacity: 0,
      transform: 'translate3d(0, 0 ,0)',
      config: { duration: 100 },
    },
    exitBeforeEnter: true,
  });

  return (
    <>
      {transition((styles, currentCard) => {
        const gameplayTier =
          currentCard.anyPlayer.gameplayTier ?? GameplayTier.DNP;
        const openPlayerDetails = showPlayerDetails
          ? () => {
              setPlayerDetailsOpen(true);
            }
          : undefined;
        return (
          <animated.div className={footerWrapper} style={{ ...styles, zIndex }}>
            {showNBACardFooter ? (
              <NBAAnyCardFooter
                anyCard={currentCard}
                onClick={openPlayerDetails}
              />
            ) : (
              <CardFooter
                left={
                  <AnyPlayerStatsIcon
                    backgroundColor="transparent"
                    player={currentCard.anyPlayer}
                  />
                }
                right={<GameplayTierStars gameplayTier={gameplayTier} />}
                onClick={openPlayerDetails}
              >
                <PlayerScore
                  player={currentCard.anyPlayer}
                  mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
                  animated={false}
                  hideTooltip
                />
              </CardFooter>
            )}
          </animated.div>
        );
      })}
      <PlayerDrawer
        cardOpened={
          showPlayerDetails && playerDetailsOpen
            ? {
                playerSlug: card.anyPlayer.slug,
                cardSlug: card.slug,
              }
            : null
        }
        onClose={() => {
          setPlayerDetailsOpen(false);
        }}
      />
    </>
  );
};

CardInfos.fragments = {
  anyCardInterface: gql`
    fragment CardInfos_anyCardInterface on AnyCardInterface {
      slug
      anyPlayer {
        slug
        gameplayTier
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
      ...NBAAnyCardFooter_anyCardInterface
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
    ${NBAAnyCardFooter.fragments.anyCardInterface}
  ` as TypedDocumentNode<CardInfos_anyCardInterface>,
};
